import clsx from 'clsx';
import { Link, useNavigate } from 'react-router';
import { Avatar, Icon, IconButton, LogoMark } from '@/design-system';
import { ns, useT } from '@/i18n';
import { usePartnerStatus, useSession } from '@/services/queries';
import { paths } from '../paths';
import { usePreferences } from '../state/preferences';
import { useOverlays } from '../state/overlays';
import { SearchPill } from './SearchPill';
import type { ShellHandle } from './shellConfig';
import { UniverseSwitch } from './UniverseSwitch';
import { useUnreadCount } from './useUnread';
import type { ScrollState } from './useScrollState';
import styles from './DesktopHeader.module.css';

/** Fixed 84 px glass header (`.hd`), ≥ 768 px. */
export function DesktopHeader({ handle, scroll }: { handle: ShellHandle; scroll: ScrollState }) {
  const navigate = useNavigate();
  const t = useT(ns.shell);
  const tc = useT(ns.common);
  const { data: user } = useSession();
  // Approved partners get «Mode Agence», the external agency dashboard (API-AUTH-11, P13).
  const { data: partner } = usePartnerStatus(!!user);
  const dashboard = partner?.status === 'approved' ? partner.dashboardUrl : null;
  const { language, currency } = usePreferences();
  const overlays = useOverlays();
  const unread = useUnreadCount();
  const over = !!handle.hero && !scroll.scrolled;
  const showSwitch = handle.center === 'switch' || (handle.center === 'switch-until-deep' && !scroll.deep);
  const showPill = handle.center === 'pill' || (handle.center === 'switch-until-deep' && scroll.deep);
  const isExp = handle.universe === 'exp';

  return (
    <header className={clsx(styles.hd, over && styles.over, scroll.scrolled && styles.lifted)}>
      <Link to={paths.home} className={styles.logo} aria-label={tc('brand.home')}>
        <LogoMark size={40} inverted={over} />
        <span className={clsx('t-display', styles.word)}>Cardnd</span>
      </Link>

      <div className={styles.center}>
        {showSwitch && <UniverseSwitch universe={handle.universe} overHero={over} />}
        {showPill && <SearchPill />}
        {handle.center === 'secure' && (
          <span className={styles.secure}>
            <Icon name="lock" size={16} strokeWidth={2} />
            {t('securePayment')}
          </span>
        )}
      </div>

      <div className={styles.actions}>
        {!showSwitch && (
          <Link to={isExp ? paths.home : paths.experiences} className={clsx(styles.link, styles.uniLink)}>
            {isExp ? tc('vehicles') : tc('experiences')}
          </Link>
        )}
        {showSwitch &&
          (dashboard ? (
            <a
              className={clsx(styles.link, styles.partnerLink)}
              href={dashboard}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('agencyMode')}
            </a>
          ) : (
            <Link to={paths.partner} className={clsx(styles.link, styles.partnerLink)}>
              {t('becomePartner')}
            </Link>
          ))}
        <button
          type="button"
          className={styles.pref}
          aria-haspopup="dialog"
          aria-expanded={overlays.current === 'preferences'}
          aria-label={t('preferences')}
          onClick={() => overlays.toggle('preferences')}
        >
          <Icon name="globe" size={16} strokeWidth={1.8} />
          <span className={styles.prefText}>
            {language.toUpperCase()} · {currency}
          </span>
        </button>
        <IconButton
          icon="chat"
          label={t('messages.unread', { count: unread })}
          badge={unread}
          overHero={over}
          onClick={() => navigate(paths.messages)}
        />
        <button
          type="button"
          className={styles.acc}
          aria-haspopup="menu"
          aria-expanded={overlays.current === 'account'}
          aria-label={t('accountMenu')}
          onClick={() => overlays.toggle('account')}
        >
          <Icon name="menu" size={18} strokeWidth={2} />
          <Avatar name={user?.name} size={32} />
        </button>
      </div>
    </header>
  );
}
